import React, { useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import type { StrapiQuotationOtherCostRow } from '../../../../api/strapiCrm';
import type { QuotationFormOtherCostRow, QuotationFormSampleRow, QuotationFormValues } from '../quotationFormTypes';
import type { QuotationFormGroupOtherCostSubtotal, QuotationFormGroupSampleSubtotal } from '../quotationFormGroups';
import { quotationFormSampleLineSubtotal } from '../quotationFormSampleMoney';
import {
  QUOTATION_MANAJER_APPROVAL_THRESHOLD,
  quotationRequiresManajerApproval,
} from '../../quotationGrandTotal';

export { quotationFormSampleLineSubtotal } from '../quotationFormSampleMoney';

export function quotationFormOtherCostsFromDetailLines(
  list: StrapiQuotationOtherCostRow[],
): QuotationFormOtherCostRow[] {
  return list.map((r, i) => ({
    key: r.documentId || `srv-c-${i}`,
    documentId: r.documentId,
    description: r.description ?? '',
    quantity:
      r.quantity != null && Number.isFinite(Number(r.quantity)) ? String(Math.max(1, Number(r.quantity))) : '1',
    cost_price:
      r.cost_price != null && Number.isFinite(Number(r.cost_price)) ? String(r.cost_price) : '',
  }));
}

function rowHasAnyField(row: QuotationFormOtherCostRow): boolean {
  return Boolean(row.description.trim() || row.cost_price.trim());
}

function validateCompleteRow(
  row: QuotationFormOtherCostRow,
  indexLabel: number,
):
  | { valid: false; message: string }
  | { valid: true; description: string; quantity: number; cost_price: number } {
  const description = row.description.trim();
  if (!description) {
    return { valid: false, message: `Baris ${indexLabel}: deskripsi wajib diisi.` };
  }
  const q = Number.parseInt(row.quantity.trim(), 10);
  if (!Number.isFinite(q) || q < 1) {
    return { valid: false, message: `Baris ${indexLabel}: kuantitas minimal 1.` };
  }
  const pTrim = row.cost_price.trim();
  const p = Number.parseFloat(pTrim.replace(',', '.'));
  if (!Number.isFinite(p) || p < 0) {
    return { valid: false, message: `Baris ${indexLabel}: harga satuan harus angka ≥ 0.` };
  }
  return { valid: true, description, quantity: q, cost_price: p };
}

export type QuotationOtherCostsSubmitValidatedLine = {
  kind: 'create';
  payload: { description: string; quantity: number; cost_price: number };
};

export type QuotationOtherCostsPersistedUpdateLine = {
  kind: 'update';
  documentId: string;
  payload: { description: string; quantity: number; cost_price: number };
};

export function validateQuotationFormOtherCostsForSave(rows: QuotationFormOtherCostRow[]):
  | { ok: false; message: string }
  | { ok: true; creates: QuotationOtherCostsSubmitValidatedLine[]; updates: QuotationOtherCostsPersistedUpdateLine[] } {
  const creates: QuotationOtherCostsSubmitValidatedLine[] = [];
  const updates: QuotationOtherCostsPersistedUpdateLine[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const label = i + 1;
    if (row.documentId) {
      const v = validateCompleteRow(row, label);
      if (!v.valid) return { ok: false, message: v.message };
      updates.push({
        kind: 'update',
        documentId: row.documentId,
        payload: {
          description: v.description,
          quantity: v.quantity,
          cost_price: v.cost_price,
        },
      });
      continue;
    }
    if (!rowHasAnyField(row)) continue;
    const v = validateCompleteRow(row, label);
    if (!v.valid) return { ok: false, message: v.message };
    creates.push({
      kind: 'create',
      payload: {
        description: v.description,
        quantity: v.quantity,
        cost_price: v.cost_price,
      },
    });
  }

  return { ok: true, creates, updates };
}

/** Subtotal baris biaya lain = kuantitas × harga satuan × multiplier jadwal berulang (kontrak). */
export function quotationFormOtherCostLineSubtotal(
  row: QuotationFormOtherCostRow,
  recurringCount = 1,
): number {
  const q = Number.parseInt(row.quantity.trim(), 10);
  const qty = Number.isFinite(q) && q >= 1 ? q : 0;
  const pTrim = row.cost_price.trim();
  const p = Number.parseFloat(pTrim.replace(',', '.'));
  const price = pTrim !== '' && Number.isFinite(p) && p >= 0 ? p : 0;
  const mult = recurringCount >= 1 ? recurringCount : 1;
  return qty * price * mult;
}

function otherCostLineSubtotalsFromRows(
  rows: QuotationFormOtherCostRow[],
  recurringCounts?: number[],
): number[] {
  return rows.map((row, index) =>
    quotationFormOtherCostLineSubtotal(row, recurringCounts?.[index] ?? 1),
  );
}

export function quotationFormOtherCostsSubtotalFromRows(
  rows: QuotationFormOtherCostRow[],
  recurringCount = 1,
): number {
  return rows.reduce((sum, row) => sum + quotationFormOtherCostLineSubtotal(row, recurringCount), 0);
}

/** Nilai diskon rupiah efektif (dibatasi tidak melebihi subtotal). */
export function computeEffectiveDiscountRupiah(
  subtotalBeforeDiscount: number,
  discountIsPercentage: boolean,
  discountAmountStr: string,
  discountPercentageStr: string,
): number {
  const cap = Math.max(0, Math.round(subtotalBeforeDiscount));
  if (discountIsPercentage) {
    const raw = discountPercentageStr.trim().replace(',', '.');
    const pct = Number.parseFloat(raw);
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    const clamped = Math.min(100, Math.max(0, pct));
    const rupiah = Math.round(cap * (clamped / 100));
    return Math.min(rupiah, cap);
  }
  const n = Number.parseInt(discountAmountStr.trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, cap);
}

export function quotationFormSubtotalBeforeDiscount(
  sampleRows: QuotationFormSampleRow[],
  otherCostRows: QuotationFormOtherCostRow[],
  opts?: { sampleRecurringCounts?: number[]; otherCostRecurringCounts?: number[] },
): number {
  const counts = opts?.sampleRecurringCounts;
  const samplesSubtotal = sampleRows
    .map((row, i) => quotationFormSampleLineSubtotal(row, counts?.[i] ?? 1))
    .reduce((a, b) => a + b, 0);
  const otherCostsSubtotal = otherCostLineSubtotalsFromRows(
    otherCostRows,
    opts?.otherCostRecurringCounts,
  ).reduce((a, b) => a + b, 0);
  return samplesSubtotal + otherCostsSubtotal;
}

export function quotationFormMoneyTotals(opts: {
  sampleRows: QuotationFormSampleRow[];
  otherCostRows: QuotationFormOtherCostRow[];
  discountIsPercentage: boolean;
  discountAmountStr: string;
  discountPercentageStr: string;
  isPpn: boolean;
  sampleRecurringCounts?: number[];
  otherCostRecurringCounts?: number[];
}): {
  sampleLineSubtotals: number[];
  samplesSubtotal: number;
  otherCostLineSubtotals: number[];
  otherCostsSubtotal: number;
  subtotalBeforeDiscount: number;
  discountAppliedRupiah: number;
  afterDiscount: number;
  ppnAmount: number;
  grandTotal: number;
} {
  const sampleLineSubtotals = opts.sampleRows.map((row, i) =>
    quotationFormSampleLineSubtotal(row, opts.sampleRecurringCounts?.[i] ?? 1),
  );
  const samplesSubtotal = sampleLineSubtotals.reduce((a, b) => a + b, 0);

  const otherCostLineSubtotals = otherCostLineSubtotalsFromRows(
    opts.otherCostRows,
    opts.otherCostRecurringCounts,
  );
  const otherCostsSubtotal = otherCostLineSubtotals.reduce((a, b) => a + b, 0);

  const subtotalBeforeDiscount = samplesSubtotal + otherCostsSubtotal;
  const discountAppliedRupiah = computeEffectiveDiscountRupiah(
    subtotalBeforeDiscount,
    opts.discountIsPercentage,
    opts.discountAmountStr,
    opts.discountPercentageStr,
  );
  const afterDiscount = Math.max(0, subtotalBeforeDiscount - discountAppliedRupiah);
  const ppnAmount = opts.isPpn ? Math.round(afterDiscount * 0.11) : 0;
  const grandTotal = afterDiscount + ppnAmount;
  return {
    sampleLineSubtotals,
    samplesSubtotal,
    otherCostLineSubtotals,
    otherCostsSubtotal,
    subtotalBeforeDiscount,
    discountAppliedRupiah,
    afterDiscount,
    ppnAmount,
    grandTotal,
  };
}

function formatIdrSignedPlus(n: number): string {
  return `+ Rp ${n.toLocaleString('id-ID', { maximumFractionDigits: 0 })}`;
}

function formatIdrSignedMinus(n: number): string {
  return `− Rp ${n.toLocaleString('id-ID', { maximumFractionDigits: 0 })}`;
}

function formatPercentLabel(raw: string): string {
  const t = raw.trim().replace(',', '.');
  const p = Number.parseFloat(t);
  if (!Number.isFinite(p)) return raw.trim();
  return p.toLocaleString('id-ID', { maximumFractionDigits: 2 });
}

function formatIdr(n: number): string {
  return n.toLocaleString('id-ID', { maximumFractionDigits: 0 });
}

type Props = {
  disabled?: boolean;
  /** Sembunyikan tombol tambah/ubah/hapus biaya lain (mode lihat). */
  readOnly?: boolean;
  sampleRows: QuotationFormSampleRow[];
  sampleRecurringCounts?: number[];
  /** Subtotal sampel per tab group (quotation kontrak/konsultan). */
  groupSampleSubtotals?: QuotationFormGroupSampleSubtotal[];
  /** Subtotal biaya lain per group (untuk ringkasan grand total grouped). */
  groupOtherCostSubtotals?: QuotationFormGroupOtherCostSubtotal[];
  /** Semua baris biaya lain (grouped: gabungan semua tab; default: otherCostRows). */
  allOtherCostRowsForGrandTotal?: QuotationFormOtherCostRow[];
  /** Multiplier jadwal berulang selaras allOtherCostRowsForGrandTotal. */
  allOtherCostRecurringCounts?: number[];
  /** Multiplier jadwal berulang untuk baris biaya lain yang sedang ditampilkan. */
  otherCostRecurringCount?: number;
  discount_amount: string;
  discount_is_percentage: boolean;
  discount_percentage: string;
  is_ppn: boolean;
  otherCostRows: QuotationFormOtherCostRow[];
  onPatch: (
    patch: Partial<
      Pick<
        QuotationFormValues,
        'discount_amount' | 'discount_is_percentage' | 'discount_percentage' | 'is_ppn' | 'otherCostRows'
      >
    >,
  ) => void;
  onNewOtherCost: () => void;
  onEditOtherCost: (index: number) => void;
  onDeleteOtherCost: (index: number) => void;
  /** Tampilkan diskon, PPN, dan ringkasan grand total (default true). */
  showGrandTotalSummary?: boolean;
};

/** Tabel biaya lain (baca-saja) + diskon + PPN + ringkasan total; aksi lewat modal di parent. */
export function QuotationFormBiayaLainnyaTabScreen({
  disabled = false,
  readOnly = false,
  sampleRows,
  sampleRecurringCounts,
  groupSampleSubtotals,
  groupOtherCostSubtotals,
  allOtherCostRowsForGrandTotal,
  allOtherCostRecurringCounts,
  otherCostRecurringCount,
  discount_amount,
  discount_is_percentage,
  discount_percentage,
  is_ppn,
  otherCostRows,
  onPatch,
  onNewOtherCost,
  onEditOtherCost,
  onDeleteOtherCost,
  showGrandTotalSummary = true,
}: Props) {
  const otherCostRowsForTotals =
    showGrandTotalSummary && allOtherCostRowsForGrandTotal
      ? allOtherCostRowsForGrandTotal
      : otherCostRows;

  const {
    samplesSubtotal,
    otherCostsSubtotal,
    subtotalBeforeDiscount,
    discountAppliedRupiah,
    afterDiscount,
    ppnAmount,
    grandTotal,
  } = useMemo(
    () =>
      quotationFormMoneyTotals({
        sampleRows,
        otherCostRows: otherCostRowsForTotals,
        discountIsPercentage: discount_is_percentage,
        discountAmountStr: discount_amount,
        discountPercentageStr: discount_percentage,
        isPpn: is_ppn,
        sampleRecurringCounts,
        otherCostRecurringCounts:
          showGrandTotalSummary && allOtherCostRowsForGrandTotal
            ? allOtherCostRecurringCounts
            : otherCostRows.map(() => otherCostRecurringCount ?? 1),
      }),
    [
      sampleRows,
      otherCostRowsForTotals,
      discount_is_percentage,
      discount_amount,
      discount_percentage,
      is_ppn,
      sampleRecurringCounts,
      showGrandTotalSummary,
      allOtherCostRowsForGrandTotal,
      allOtherCostRecurringCounts,
      otherCostRows,
      otherCostRecurringCount,
    ],
  );

  const visibleOtherCostLineSubtotals = useMemo(
    () => otherCostRows.map((row) => quotationFormOtherCostLineSubtotal(row, otherCostRecurringCount ?? 1)),
    [otherCostRows, otherCostRecurringCount],
  );

  const showCostActions = !readOnly;

  return (
    <Stack spacing={2} sx={{ maxWidth: 920 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Biaya lainnya
      </Typography>
      {!readOnly ? (
        <Typography variant="body2" color="text.secondary">
          Biaya di luar baris sampel. Subtotal per baris = kuantitas × harga satuan
          {otherCostRecurringCount != null && otherCostRecurringCount > 1
            ? ` × ${otherCostRecurringCount} jadwal berulang`
            : ''}
          . Ringkasan di bawah memakai tanda{' '}
          <strong>+</strong> untuk penjumlahan, <strong>−</strong> untuk diskon, dan <strong>+</strong> untuk PPN 11% bila
          diaktifkan. Diskon bisa diisi sebagai nominal rupiah atau persentase dari jumlah sebelum diskon.
        </Typography>
      ) : null}

      {showCostActions ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={() => onNewOtherCost()}
            data-testid="quotation-form-new-other-cost"
          >
            Biaya lainnya baru
          </Button>
        </Stack>
      ) : null}

      <Table size="small" sx={{ '& .MuiTableCell-root': { verticalAlign: 'middle', py: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Deskripsi</TableCell>
            <TableCell width={110}>Kuantitas</TableCell>
            <TableCell width={140}>Harga satuan</TableCell>
            <TableCell width={140} align="right">
              Subtotal
            </TableCell>
            {showCostActions ? (
              <TableCell width={100} align="center">
                Aksi
              </TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {otherCostRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showCostActions ? 6 : 5}>
                <Typography variant="body2" color="text.secondary">
                  Belum ada biaya lainnya. Klik &quot;Biaya lainnya baru&quot; untuk menambah.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            otherCostRows.map((row, i) => (
              <TableRow key={row.key}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2">{row.description || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{row.quantity || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.cost_price.trim()
                      ? formatIdr(Number.parseFloat(row.cost_price.replace(',', '.')) || 0)
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {(visibleOtherCostLineSubtotals[i] ?? 0) > 0
                      ? formatIdr(visibleOtherCostLineSubtotals[i] ?? 0)
                      : '—'}
                  </Typography>
                </TableCell>
                {showCostActions ? (
                  <TableCell align="center">
                    <Tooltip title="Ubah">
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Ubah biaya baris ${i + 1}`}
                          disabled={disabled}
                          onClick={() => onEditOtherCost(i)}
                          data-testid={`quotation-form-other-cost-edit-${i}`}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Hapus">
                      <span>
                        <IconButton
                          size="small"
                          color="warning"
                          aria-label={`Hapus biaya baris ${i + 1}`}
                          disabled={disabled}
                          onClick={() => onDeleteOtherCost(i)}
                          data-testid={`quotation-form-other-cost-delete-${i}`}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {showGrandTotalSummary ? (
        <>
      <Stack spacing={2} sx={{ maxWidth: 440 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Mode diskon
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={discount_is_percentage ? 'percent' : 'fixed'}
          onChange={(_, v: 'fixed' | 'percent' | null) => {
            if (v == null) return;
            onPatch({ discount_is_percentage: v === 'percent' });
          }}
          disabled={disabled}
          aria-label="Mode diskon"
        >
          <ToggleButton value="fixed" data-testid="quotation-form-discount-mode-fixed">
            Nominal (Rp)
          </ToggleButton>
          <ToggleButton value="percent" data-testid="quotation-form-discount-mode-percent">
            Persentase (%)
          </ToggleButton>
        </ToggleButtonGroup>
        {discount_is_percentage ? (
          <TextField
            label="Diskon (persentase)"
            type="number"
            size="small"
            fullWidth
            value={discount_percentage}
            onChange={(e) => onPatch({ discount_percentage: e.target.value })}
            disabled={disabled}
            helperText="0–100% dari jumlah sebelum diskon. Nilai rupiah dikirim ke server dihitung otomatis saat simpan."
            inputProps={{ min: 0, max: 100, step: 0.01, 'data-testid': 'quotation-form-discount-percent' }}
          />
        ) : (
          <TextField
            label="Diskon (rupiah)"
            type="number"
            size="small"
            fullWidth
            value={discount_amount}
            onChange={(e) => onPatch({ discount_amount: e.target.value })}
            disabled={disabled}
            helperText="Pengurangan tetap (tidak melebihi jumlah sebelum diskon)."
            inputProps={{ min: 0, 'data-testid': 'quotation-form-discount-amount' }}
          />
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={is_ppn}
              onChange={(e) => onPatch({ is_ppn: e.target.checked })}
              disabled={disabled}
              slotProps={{
                input: { 'data-testid': 'quotation-form-is-ppn' } as React.InputHTMLAttributes<HTMLInputElement>,
              }}
            />
          }
          label="PPN (11% dari jumlah setelah diskon)"
        />
      </Stack>

      <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, maxWidth: 480 }}>
        <Stack spacing={0.75}>
          {groupSampleSubtotals && groupSampleSubtotals.length > 0
            ? groupSampleSubtotals.map((g) => (
                <Stack
                  key={g.groupKey}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                >
                  <Typography variant="body2" color="text.secondary">
                    + Total sampel Sampel ({g.groupIndex + 1})
                    {g.recurringCount > 1 ? ` × ${g.recurringCount} jadwal` : ''}
                  </Typography>
                  <Typography variant="body2">{formatIdrSignedPlus(g.subtotal)}</Typography>
                </Stack>
              ))
            : null}
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" color="text.secondary">
              + Total sampel
              {groupSampleSubtotals && groupSampleSubtotals.length > 1 ? ' (semua group)' : ''}
            </Typography>
            <Typography variant="body2">{formatIdrSignedPlus(samplesSubtotal)}</Typography>
          </Stack>
          {groupOtherCostSubtotals && groupOtherCostSubtotals.length > 0
            ? groupOtherCostSubtotals.map((g) => (
                <Stack
                  key={`other-${g.groupKey}`}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                >
                  <Typography variant="body2" color="text.secondary">
                    + Total biaya lainnya Sampel ({g.groupIndex + 1})
                    {g.recurringCount > 1 ? ` × ${g.recurringCount} jadwal` : ''}
                  </Typography>
                  <Typography variant="body2">{formatIdrSignedPlus(g.subtotal)}</Typography>
                </Stack>
              ))
            : null}
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" color="text.secondary">
              + Total biaya lainnya
              {groupOtherCostSubtotals && groupOtherCostSubtotals.length > 1 ? ' (semua group)' : ''}
            </Typography>
            <Typography variant="body2">{formatIdrSignedPlus(otherCostsSubtotal)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Jumlah sebelum diskon
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Rp {formatIdr(subtotalBeforeDiscount)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" color="text.secondary">
              {discount_is_percentage
                ? `− Diskon (${formatPercentLabel(discount_percentage)}%)`
                : '− Diskon (nominal)'}
            </Typography>
            <Typography variant="body2" color={discountAppliedRupiah > 0 ? 'error.main' : 'text.secondary'}>
              {formatIdrSignedMinus(discountAppliedRupiah)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Jumlah setelah diskon
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Rp {formatIdr(afterDiscount)}
            </Typography>
          </Stack>
          {is_ppn ? (
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" color="text.secondary">
                + PPN 11%
              </Typography>
              <Typography variant="body2" color="success.main">
                {formatIdrSignedPlus(ppnAmount)}
              </Typography>
            </Stack>
          ) : null}
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ pt: 1 }}>
            <Typography variant="subtitle2">Grand total</Typography>
            <Typography variant="subtitle2" data-testid="quotation-form-other-cost-grand-total">
              Rp {formatIdr(grandTotal)}
            </Typography>
          </Stack>
          {quotationRequiresManajerApproval(grandTotal) ? (
            <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="quotation-form-manajer-approval-hint">
              Total quotation ≥ Rp {formatIdr(QUOTATION_MANAJER_APPROVAL_THRESHOLD)} — setelah disetujui SPV,
              status akan menunggu persetujuan manajer marketing sebelum pelanggan dapat disetujui.
            </Alert>
          ) : null}
        </Stack>
      </Box>
        </>
      ) : null}
    </Stack>
  );
}
